import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const AboutContainer = styled.section`
  padding: 6rem 0;
  background-color: var(--white);
  scroll-margin-top: 70px;
  
  @media (max-width: 768px) {
    padding: 4rem 0;
    scroll-margin-top: 60px;
  }
  
  @media (max-width: 480px) {
    padding: 3rem 0;
  }
  
  .dark-mode & {
    background-color: var(--dark);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    padding: 0 1.5rem;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--primary);
  position: relative;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.7rem;
  }
  
  .dark-mode & {
    color: var(--white);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: var(--primary);
    
    .dark-mode & {
      background-color: var(--secondary);
    }
  }
`;

const SectionDescription = styled.p`
  color: var(--secondary);
  font-size: 1.1rem;
  line-height: 1.6;
  
  .dark-mode & {
    color: var(--light);
    opacity: 0.8;
  }
`;

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
  
  @media (max-width: 768px) {
    padding: 0 1.5rem;
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const AboutImage = styled.div`
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: var(--shadow-lg);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 5px solid var(--primary);
    border-radius: 8px;
    z-index: -1;
  }
  
  @media (max-width: 992px) {
    height: 400px;
  }
`;

const AboutInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const AboutTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: var(--dark);
  
  .dark-mode & {
    color: var(--white);
  }
`;

const AboutText = styled.p`
  color: var(--secondary);
  margin-bottom: 2rem;
  line-height: 1.8;
  
  .dark-mode & {
    color: var(--light);
    opacity: 0.8;
  }
`;

const AboutSection = () => {
  return (
    <AboutContainer id="a-propos">
      <SectionHeader>
        <SectionTitle className="section-title">À Propos</SectionTitle>
      </SectionHeader>
      
      <AboutContent>
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <AboutImage>
            <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80" alt="Notre équipe" />
          </AboutImage>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <AboutInfo>
            <AboutTitle>Développeur web passionné par l'innovation</AboutTitle>
            <AboutText>
              GVW-Tech est une entreprise spécialisée dans le développement web, fondée par un développeur passionné par les technologies modernes et l'innovation.
            </AboutText>
            <AboutText>
              Notre mission est de créer des solutions web sur mesure qui répondent parfaitement aux besoins de nos clients, tout en offrant une expérience utilisateur exceptionnelle et des performances optimales.
            </AboutText>
          </AboutInfo>
        </motion.div>
      </AboutContent>
    </AboutContainer>
  );
};

export default AboutSection;
